import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
} from "./cycle-configurations.api";

import type {
  Configuration,
  CreateConfigurationPayload,
  UpdateConfigurationPayload,
} from "@/common/types/Cycle";

export const useCreateConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      payload: CreateConfigurationPayload & { workspace_version_id: number },
    ) => createConfiguration(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
};

export const useUpdateConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      payload,
    }: {
      configId: number;
      payload: UpdateConfigurationPayload;
    }) => updateConfiguration(configId, payload),
    onMutate: async ({ configId, payload }) => {
      await queryClient.cancelQueries({ queryKey: ["configurations"] });
      const prev = queryClient.getQueriesData<Configuration[]>({
        queryKey: ["configurations"],
      });
      queryClient.setQueriesData<Configuration[]>(
        { queryKey: ["configurations"] },
        (old) =>
          old?.map((c) =>
            c.id === configId ? ({ ...c, ...payload } as Configuration) : c,
          ),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.prev.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
};

export const useDeleteConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: number) => deleteConfiguration(configId),
    onMutate: async (configId) => {
      await queryClient.cancelQueries({ queryKey: ["configurations"] });
      const prev = queryClient.getQueriesData<Configuration[]>({
        queryKey: ["configurations"],
      });
      queryClient.setQueriesData<Configuration[]>(
        { queryKey: ["configurations"] },
        (old) => old?.filter((c) => c.id !== configId),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.prev.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["configurations"] });
    },
  });
};
